import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | pending | failed | not_found
  const [err, setErr] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("not_found"); return; }
    let alive = true;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!alive) return;
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
        tries.current += 1;
        if (tries.current >= 12) { setStatus("pending"); return; } // ~24s max
        setTimeout(poll, 2000);
      } catch (e) {
        if (!alive) return;
        setErr(formatApiError(e));
        setStatus("failed");
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <div className="fade-in max-w-lg mx-auto rounded-md border border-stone-200 bg-white p-10 text-center" data-testid="payment-return">
      {status === "polling" && (
        <>
          <Loader2 className="h-10 w-10 text-primary mx-auto mb-4 animate-spin" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-stone-900">Confirming your payment…</h1>
          <p className="mt-3 text-stone-600">Please stay on this page — it takes a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" strokeWidth={1.5} data-testid="paid-icon" />
          <h1 className="font-serif text-3xl text-stone-900">Thank you!</h1>
          <p className="mt-3 text-stone-700 leading-relaxed">
            Your order is confirmed and payment received. We'll email you a receipt and shipping updates.
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/tracts"><Button variant="outline">Order more</Button></Link>
            <Link to="/"><Button>Back home</Button></Link>
          </div>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 className="h-10 w-10 text-stone-400 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-stone-900">Payment is processing</h1>
          <p className="mt-3 text-stone-600">Your bank is still confirming. We'll email you the moment it clears.</p>
          <div className="mt-6"><Link to="/"><Button variant="outline">Back home</Button></Link></div>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" strokeWidth={1.5} data-testid="failed-icon" />
          <h1 className="font-serif text-3xl text-stone-900">Payment didn't complete</h1>
          <p className="mt-3 text-stone-700">{err || "Your card was not charged. Please try again."}</p>
          <div className="mt-6"><Link to="/tracts"><Button>Try again</Button></Link></div>
        </>
      )}
      {status === "not_found" && (
        <>
          <XCircle className="h-12 w-12 text-stone-400 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-stone-900">No session found</h1>
          <div className="mt-6"><Link to="/tracts"><Button>Back to tracts</Button></Link></div>
        </>
      )}
    </div>
  );
}
